import React, { useMemo } from 'react';
import { FormikProvider, useFormik } from 'formik';
import { useParams } from 'react-router-dom';
import { Button, Check, Modal } from 'edozvola-frontend-lib/dist/ui/components';
import { Col, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { Loader } from 'app/shared/loader/loader';
import { Label } from 'edozvola-frontend-lib/dist/ui';
import { saveCaseZoningPlanQuery } from 'app/edozvola/cases/case-zoning-plan-compliance/service/case-zoning-plan-compliance.queries';
import { caseZoningPlanFilterInitialData } from 'app/edozvola/cases/case-zoning-plan-compliance/model/case-zoning-plan-compliance.model';
import { caseZoningPlanValidationSchema } from 'app/edozvola/cases/case-zoning-plan-compliance/model/case-zoning-plan-compliance.schema';
import ViewTable from 'app/shared/view-table/view-table';
import ViewTableHeaderColumn from 'app/shared/view-table/view-table-header-column';
import { ZoningPlanViewModel } from 'app/shared/model/shared.model';
import {
  fetchCityMunicipalitiesAndCountiesForCaseQuery,
  fetchZoningPlansQuery,
  fetchZoningPlanTypesQuery
} from 'app/edozvola/codebooks/codebooks.queries';
import {PaginatedDropdown} from "edozvola-frontend-lib/dist/ui/components/paginated-dropdown";

export interface ICaseZoningPlanFormProps {
  idCaseZoningPlan: string | null;
  setIdCaseZoningPlan: any;
  readOnly: boolean;
}

const CaseZoningPlanForm = (props: ICaseZoningPlanFormProps) => {
  const { id: uuid } = useParams<{ id: string }>();

  const [checked, setChecked] = React.useState([]);

  const { data: zoningPlanOptions, isLoading: loadingZoningPlanOptions } = fetchCityMunicipalitiesAndCountiesForCaseQuery(uuid);
  const { data: zoningPlanTypeOptions, isLoading: zoningPlanTypeOptionsLoading } = fetchZoningPlanTypesQuery();

  const formik = useFormik({
    initialValues: { countyCityMunicipality: zoningPlanOptions, zoningPlanType: zoningPlanTypeOptions } || caseZoningPlanFilterInitialData,
    onSubmit: values => {},
    validationSchema: caseZoningPlanValidationSchema,
    enableReinitialize: true,
    validateOnMount: true,
  });

  const { mutate: saveCaseZoningPlanMutation, isPending: savingCaseZoningPlan } = saveCaseZoningPlanQuery(uuid, () => closeModal());

  const closeModal = () => {
    props.setIdCaseZoningPlan(null);
    formik.resetForm({ values: caseZoningPlanFilterInitialData });
  };

  const setCheckedZoningPlan = (idZoningPlan: number) => {
    const checkedIndex = checked.findIndex(id => id === idZoningPlan);
    if (checkedIndex !== -1) {
      checked.splice(checkedIndex, 1);
    } else {
      checked.push(idZoningPlan);
    }
    setChecked([...checked]);
  };

  const submit = () => {
    saveCaseZoningPlanMutation({ zoningPlanIds: checked });
  };

  const renderTableRow = (value: ZoningPlanViewModel, zoningPlanIndex: number, totalItems: number) => {
    return (
      <tr key={value.idZoningPlan}>
        <td
          onClick={e => {
            if (value.selectable) {
              e.stopPropagation();
              setCheckedZoningPlan(value.idZoningPlan);
            }
          }}
        >
          {value.selectable && (
            <Check
              checked={checked.find(item => item === value.idZoningPlan)}
              id={value.idZoningPlan.toString()}
              className={'hand font-size-large'}
            />
          )}
        </td>
        <td>
          <span>{value.mark}</span>
          <br />
          <span>{value.name}</span>
        </td>
        <td>
          <span>{value.revisionMark}</span>
          <br />
          <span>{value.revisionName}</span>
        </td>
        <td>{value.bulletin}</td>
        <td>{value.publishingYear}</td>
      </tr>
    );
  };

  const fetchDataProps = useMemo(() => ({
    uuid: uuid,
    countyIds: formik.values.countyCityMunicipality?.filter(item => item.id.startsWith('C')).map(item => item.id.slice(1)),
    cityMunicipalityIds: formik.values.countyCityMunicipality?.filter(item => item.id.startsWith('M')).map(item => item.id.slice(1)),
    zoningPlanTypeIds: formik.values.zoningPlanType?.map(item => item.id),
  }), [uuid, formik.values.zoningPlanType, formik.values.countyCityMunicipality]);

  return (
    <>
      <Modal scrollable size={'xl'} isOpen={props.idCaseZoningPlan !== null}>
        <ModalHeader>{'Dodavanje prostornih planova'}</ModalHeader>
        <ModalBody>
          <FormikProvider value={formik}>
            {loadingZoningPlanOptions && <Loader />}
            {!loadingZoningPlanOptions && (
              <div className={'d-flex justify-content-between flex-column gap-3'}>
                <div className={'form-header'}>Područje zahvata</div>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Županija, grad/općina'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi županiju, grad/općinu'}
                      onItemSelect={selected => {
                        formik.setFieldValue('countyCityMunicipality', selected);
                        setChecked([]);
                      }}
                      selected={formik.values.countyCityMunicipality}
                      labelKey={'value'}
                      options={zoningPlanOptions}
                      disabled={props.readOnly || zoningPlanOptions.length === 0}
                      error={formik.errors.countyCityMunicipality as string}
                      showErrorMessage
                      positionFixed
                      multiple
                      clearButton
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'Vrsta plana'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi vrstu plana'}
                      onItemSelect={selectedOption => formik.setFieldValue('zoningPlanType', selectedOption)}
                      selected={formik.values.zoningPlanType}
                      labelKey={'value'}
                      options={zoningPlanTypeOptions ?? []}
                      multiple
                      positionFixed
                      clearButton
                    />
                  </Col>
                </Row>
                <div className={'form-header'}>Aktualni prostorni planovi za odabrano područje zahvata</div>
                <ViewTable
                  renderTableRow={renderTableRow}
                  noDataMessage={'Nema prostornih planova.'}
                  initialPaginationColumn={'publishingYear'}
                  initialSortDirection={'desc'}
                  fetchDataQuery={fetchZoningPlansQuery}
                  fetchDataProps={fetchDataProps}
                >
                  <ViewTableHeaderColumn renderSort={false} columnLabels={['']} width={'50px'} />
                  <ViewTableHeaderColumn
                    renderSort
                    sortKey={'mark'}
                    columnLabels={['Oznaka plana', 'Naziv prostornog plana']}
                    width={'150px'}
                  />
                  <ViewTableHeaderColumn
                    renderSort
                    sortKey={'revisionMark'}
                    columnLabels={['Oznaka revizije plana', 'Naziv revizije plana']}
                    width={'150px'}
                  />
                  <ViewTableHeaderColumn
                    renderSort
                    sortKey={'bulletin'}
                    columnLabels={['Naziv i broj službenog glasila']}
                    width={'200px'}
                  />
                  <ViewTableHeaderColumn renderSort sortKey={'publishingYear'} columnLabels={['Godina objave']} width={'100px'} />
                </ViewTable>
              </div>
            )}
          </FormikProvider>
        </ModalBody>
        <ModalFooter>
          {!props.readOnly && (
            <>
              <Button label={'Odustani'} outline size={'lg'} onClick={closeModal} />
              <Button
                label={'Spremi'}
                size={'lg'}
                disabled={!formik.isValid || checked.length === 0}
                isLoading={savingCaseZoningPlan}
                color={'info'}
                onClick={() => submit()}
              />
            </>
          )}
        </ModalFooter>
      </Modal>
    </>
  );
};

export default CaseZoningPlanForm;
